import type { FC, PropsWithChildren } from 'react';
import { createContext, memo, useContext, useMemo, useRef, useState } from 'react';

import type { IUser } from 'store/declare';

import { manageMicrophoneLevel } from './helpers/getMicrophoneLevel';
import { getCurrentUserIsTalkingStateUpdater } from './helpers/useUpdateCurrentUserIsTalkingState';

interface IContext {
    /** Текущий пользователь приложения */
    currentUser: IUser | null;
    /** Прекращение отслеживания уровня звука микрофона текущего пользователя */
    disableManageMicrophoneLevel: React.MutableRefObject<() => Promise<void>>;
    /** Начать отслеживание уровня звука из микрофона и обновляем состояние "Говорит" у текущего пользователя */
    initManageMicrophoneLevel: () => void;
    /** Обновить текущего пользователя приложения */
    setCurrentUser: React.Dispatch<React.SetStateAction<IContext['currentUser']>>;
}

const Context = createContext<IContext>({
    currentUser: null,
    disableManageMicrophoneLevel: { current: () => Promise.resolve() },
    initManageMicrophoneLevel: () => undefined,
    setCurrentUser: () => undefined,
});

/** Хранилище, связанное с пользователями */
export const useCurrentUserStore = () => useContext(Context);

const CurrentUserStore: FC<PropsWithChildren> = ({ children }) => {
    const [currentUser, setCurrentUser] = useState<IContext['currentUser']>(null);
    const updateCurrentUserIsTalkingState = getCurrentUserIsTalkingStateUpdater(setCurrentUser);
    const disableManageMicrophoneLevel = useRef<IContext['disableManageMicrophoneLevel']['current']>(() => Promise.resolve());

    const contextValue = useMemo<IContext>(() => {
        return {
            currentUser,
            disableManageMicrophoneLevel,
            initManageMicrophoneLevel: () => {
                manageMicrophoneLevel(updateCurrentUserIsTalkingState)
                    .then(disableManageMicrophoneLevelFunction => {
                        disableManageMicrophoneLevel.current = disableManageMicrophoneLevelFunction;
                    })
                    // пользователь запретил использование микрофона
                    .catch(() => {
                        return Promise.resolve();
                    });
            },
            setCurrentUser,
        };
    }, [currentUser]);

    return <Context.Provider value={contextValue}>{children}</Context.Provider>;
};

export default memo(CurrentUserStore);
